import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, X, UserCog, Search, Filter, ShieldCheck, ShieldOff, Store, MapPin, ToggleLeft, ToggleRight, CheckCircle2, XCircle, Loader2, Download, Upload, FileSpreadsheet, AlertTriangle, Eye, Building2, FileText } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { formatDate } from "../lib/format";
import { Section, Skeleton, EmptyState, Pagination, usePagination, PhoneInput, isValidPhone, toPhoneE164 } from "../components/UI";
import { DokumenChips, DOKUMEN_LIST } from "../components/DokumenSupplier";

const ROLES = ["KPPG", "SPPG", "Supplier", "TPID", "Pemda", "Pemprov"];
// Role yang punya profil tambahan (Profil Usaha / Profil Instansi).
const ROLE_BERPROFIL = ["Supplier", "SPPG", "Pemda", "Pemprov"];
const ROLE_COLOR = {
  KPPG: "bg-blue-50 text-blue-700 border-blue-200",
  SPPG: "bg-amber-50 text-amber-700 border-amber-200",
  Supplier: "bg-emerald-50 text-emerald-700 border-emerald-200",
  TPID: "bg-violet-50 text-violet-700 border-violet-200",
  Pemda: "bg-pink-50 text-pink-700 border-pink-200",
  Pemprov: "bg-teal-50 text-teal-700 border-teal-200",
};

const emptyForm = { email: "", password: "", name: "", role: "SPPG", phone: "", organization: "", id_sppg: "", is_active: true };

const TABS = [
  { key: "users", label: "Manajemen User", icon: UserCog },
  { key: "suppliers", label: "Verifikasi Supplier", icon: ShieldCheck },
];

export default function UserManagementPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [showImportProfil, setShowImportProfil] = useState(false);
  const [detail, setDetail] = useState(null);   // { row, data, loading, error }

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/users");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setError(""); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ email: r.email, password: "", name: r.name, role: r.role, phone: toPhoneE164(r.phone || ""), organization: r.organization || "", id_sppg: r.id_sppg || "", is_active: r.is_active !== false });
    setError(""); setShowForm(true);
  };

  // Email akun admin bersumber dari .env sehingga tidak diubah lewat form ini.
  const emailAkunAdmin = Boolean(editing && user?.is_admin && editing.email === user.email);
  const bisaUbahEmail = Boolean(user?.is_admin) && !emailAkunAdmin;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.phone && !isValidPhone(form.phone)) {
      setError("No. HP tidak valid. Masukkan minimal 8 angka setelah +62.");
      return;
    }
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, phone: form.phone, organization: form.organization, is_active: form.is_active, id_sppg: form.role === "SPPG" ? form.id_sppg : "" };
        if (form.password) payload.password = form.password;
        // Hanya admin yang boleh mengirim perubahan email.
        if (bisaUbahEmail && form.email !== editing.email) payload.email = form.email;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", { ...form, id_sppg: form.role === "SPPG" ? form.id_sppg : null });
      }
      setShowForm(false);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const openDetail = async (r) => {
    setDetail({ row: r, data: null, loading: true, error: "" });
    try {
      const { data } = await api.get(`/users/${r.id}/profile`);
      setDetail({ row: r, data, loading: false, error: "" });
    } catch (e) {
      setDetail({ row: r, data: null, loading: false, error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus user "${r.name}"?`)) return;
    try { await api.delete(`/users/${r.id}`); await load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const exportUsersCsv = () => {
    if (rows.length === 0) return;
    const headers = ["Nama", "Email", "Role", "ID SPPG", "Organisasi", "Status", "Bergabung"];
    const csvRows = rows.map((r) => [
      r.name,
      r.email,
      r.role,
      r.id_sppg || "",
      r.organization || "",
      r.is_active ? "Aktif" : "Nonaktif",
      r.created_at ? new Date(r.created_at).toLocaleDateString("id-ID") : "",
    ]);
    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `daftar-user-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-6" data-testid="user-mgmt-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kedan-label text-slate-500">Administrasi</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Manajemen User</h1>
          <p className="text-slate-500 text-sm mt-1">Pengelolaan user, role, permission, dan verifikasi supplier.</p>
        </div>
        {activeTab === "users" && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={exportUsersCsv} className="kedan-btn-secondary" data-testid="export-user-csv">
              <Download className="w-4 h-4" /> Ekspor CSV
            </button>
            <button onClick={() => setShowImport(true)} className="kedan-btn-secondary" data-testid="import-user-btn">
              <Upload className="w-4 h-4" /> Impor User
            </button>
            <button onClick={() => setShowImportProfil(true)} className="kedan-btn-secondary" data-testid="import-profil-sppg-btn">
              <FileSpreadsheet className="w-4 h-4" /> Impor Profil SPPG
            </button>
            <button onClick={openCreate} className="kedan-btn-primary" data-testid="add-user-btn">
              <Plus className="w-4 h-4" /> Tambah User
            </button>
          </div>
        )}
      </header>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="user-mgmt-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === tab.key
                ? "bg-white text-[#045498] shadow-sm"
                : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "users" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {ROLES.map((r) => {
              const count = rows.filter((u) => u.role === r).length;
              return (
                <div key={r} className="kedan-card p-4">
                  <div className="kedan-label text-slate-500">{r}</div>
                  <div className="font-display text-2xl font-bold mt-1">{count}</div>
                </div>
              );
            })}
          </div>

          <UserTable rows={rows} loading={loading} search={search} setSearch={setSearch}
            roleFilter={roleFilter} setRoleFilter={setRoleFilter}
            openEdit={openEdit} openDetail={openDetail} remove={remove} currentUserId={user.id} />
        </>
      )}

      {activeTab === "suppliers" && <SupplierVerificationPanel />}

      {showImport && (
        <ImportUserModal onClose={() => setShowImport(false)} onDone={load} />
      )}

      {showImportProfil && (
        <ImportProfilSppgModal onClose={() => setShowImportProfil(false)} />
      )}

      {detail && <UserProfileModal detail={detail} onClose={() => setDetail(null)} />}

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="user-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="font-display font-semibold">{editing ? "Edit User" : "Tambah User Baru"}</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3">
              <Field label="Nama Lengkap" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="form-user-name" />
              <div>
                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  disabled={!!editing && !bisaUbahEmail}
                  onChange={(v) => setForm({ ...form, email: v })}
                  required
                  testid="form-user-email"
                />
                {editing && (
                  <p className="text-xs text-slate-400 mt-1">
                    {bisaUbahEmail
                      ? "Email ini dipakai user untuk login. Pastikan benar sebelum menyimpan."
                      : emailAkunAdmin
                        ? "Email akun admin diatur melalui berkas .env."
                        : "Hanya admin yang dapat mengubah email user."}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Role</label>
                <select className="kedan-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="form-user-role">
                  {ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              {/* Hanya relevan untuk satuan SPPG */}
              {form.role === "SPPG" && (
                <div data-testid="form-user-id-sppg-wrap">
                  <Field
                    label="ID SPPG"
                    value={form.id_sppg}
                    onChange={(v) => setForm({ ...form, id_sppg: v })}
                    testid="form-user-id-sppg"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Identitas satuan SPPG, harus unik antar user. Kosongkan bila belum ada.
                  </p>
                </div>
              )}
              <Field label={editing ? "Password Baru (kosongkan jika tidak diubah)" : "Password"} type="password"
                value={form.password} onChange={(v) => setForm({ ...form, password: v })} required={!editing} testid="form-user-password" />
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">No. HP</label>
                <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="form-user-phone" />
              </div>
              <Field label="Organisasi" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="form-user-org" />

              {editing && (
                <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-200 bg-slate-50" data-testid="form-user-status-toggle">
                  <div>
                    <div className="text-sm font-medium text-slate-700">Status Akun</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {form.is_active ? "User dapat login ke sistem" : "User tidak dapat login ke sistem"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: !form.is_active })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      form.is_active ? "bg-emerald-500" : "bg-slate-300"
                    }`}
                    data-testid="toggle-user-active"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        form.is_active ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              )}

              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="kedan-btn-secondary">Batal</button>
                <button type="submit" className="kedan-btn-primary" data-testid="form-user-submit">{editing ? "Simpan" : "Tambah"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== Supplier Verification Panel ========== */
export function SupplierVerificationPanel() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, verified, unverified
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/suppliers");
      setSuppliers(data);
    } catch (e) {
      console.error("Failed to load suppliers:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleVerification = async (supplier) => {
    const newStatus = !supplier.verified;
    const action = newStatus ? "memverifikasi" : "mencabut verifikasi";
    if (!window.confirm(`Apakah Anda yakin ingin ${action} supplier "${supplier.nama_usaha}"?`)) return;

    setTogglingId(supplier.id);
    try {
      await api.patch(`/admin/suppliers/${supplier.id}/verify`, { verified: newStatus });
      setSuppliers((prev) =>
        prev.map((s) => (s.id === supplier.id ? { ...s, verified: newStatus } : s))
      );
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
    setTogglingId(null);
  };

  const filtered = suppliers.filter((s) => {
    if (statusFilter === "verified" && !s.verified) return false;
    if (statusFilter === "unverified" && s.verified) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !s.nama_usaha.toLowerCase().includes(q) &&
        !(s.user_name || "").toLowerCase().includes(q) &&
        !(s.user_email || "").toLowerCase().includes(q) &&
        !(s.kota || "").toLowerCase().includes(q) &&
        !(s.jenis_produk || []).join(" ").toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const { page, setPage, perPage, changePerPage, totalPages, paged, totalItems } = usePagination(filtered, 10);

  const totalVerified = suppliers.filter((s) => s.verified).length;
  const totalUnverified = suppliers.filter((s) => !s.verified).length;

  const exportSuppliersCsv = () => {
    if (filtered.length === 0) return;
    const headers = [
      "Nama Usaha", "No HP", "Jenis Produk", "Pemilik", "Email Pemilik", "Didaftarkan Oleh",
      ...DOKUMEN_LIST.map((d) => d.label),
      "Status Verifikasi", "Diverifikasi Oleh", "Tanggal Verifikasi",
    ];
    const csvRows = filtered.map((s) => [
      s.nama_usaha,
      s.no_hp,
      (s.jenis_produk || []).join("; "),
      s.user_name || "-",
      s.user_email || "-",
      s.registered_by_name || "Mandiri",
      ...DOKUMEN_LIST.map((d) => (s.dokumen?.[d.key] ? "Ada" : "Belum")),
      s.verified ? "Terverifikasi" : "Belum Verifikasi",
      s.verified_by_name || "-",
      s.verified_at ? formatDate(s.verified_at) : "-",
    ]);
    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `verifikasi-supplier-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-4" data-testid="supplier-verification-panel">
      {/* Export Button */}
      <div className="flex justify-end">
        <button onClick={exportSuppliersCsv} className="kedan-btn-secondary" data-testid="export-supplier-verification-csv">
          <Download className="w-4 h-4" /> Ekspor CSV
        </button>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kedan-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-[#045498] flex items-center justify-center">
              <Store className="w-5 h-5" />
            </div>
            <div>
              <div className="kedan-label text-slate-500">Total Supplier</div>
              <div className="font-display text-2xl font-bold">{suppliers.length}</div>
            </div>
          </div>
        </div>
        <div className="kedan-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="kedan-label text-slate-500">Terverifikasi</div>
              <div className="font-display text-2xl font-bold text-emerald-600">{totalVerified}</div>
            </div>
          </div>
        </div>
        <div className="kedan-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <ShieldOff className="w-5 h-5" />
            </div>
            <div>
              <div className="kedan-label text-slate-500">Belum Verifikasi</div>
              <div className="font-display text-2xl font-bold text-amber-600">{totalUnverified}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="kedan-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-slate-400" />
          <div className="flex gap-1.5">
            {[
              { key: "all", label: "Semua" },
              { key: "verified", label: "Terverifikasi" },
              { key: "unverified", label: "Belum Verifikasi" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => { setStatusFilter(f.key); setPage(1); }}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                  statusFilter === f.key
                    ? "bg-[#045498] text-white border-[#045498]"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`filter-supplier-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              className="kedan-input !w-64 !py-1.5 !pl-8 text-xs"
              placeholder="Cari nama usaha, pemilik, email, kota..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              data-testid="search-supplier-verification"
            />
          </div>
          <div className="text-xs text-slate-500">{filtered.length} supplier</div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="Tidak ada supplier ditemukan" description="Coba ubah filter atau kata kunci pencarian." icon={Store} />
        ) : (<>
          <table className="kedan-table">
            <thead><tr>
              <th>Nama Usaha</th>
              <th>Jenis Produk</th>
              <th>Pemilik</th>
              <th>Didaftarkan Oleh</th>
              <th>Dokumen</th>
              <th>Status Verifikasi</th>
              <th className="text-center">Aksi</th>
            </tr></thead>
            <tbody data-testid="supplier-verification-table-body">
              {paged.map((s) => (
                <tr key={s.id} className="group">
                  <td>
                    <Link to={`/suppliers/${s.id}`} className="font-medium text-[#045498] hover:underline">
                      {s.nama_usaha}
                    </Link>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{s.no_hp}</div>
                  </td>
                  <td className="text-sm text-slate-600">{(s.jenis_produk || []).join(", ")}</td>
                  <td>
                    <div className="text-sm text-slate-900">{s.user_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{s.user_email}</div>
                  </td>
                  <td>
                    <div className="text-sm text-slate-700">
                      {s.registered_by_name ? s.registered_by_name : <span className="text-slate-400 italic">Mandiri</span>}
                    </div>
                  </td>
                  <td>
                    <DokumenChips supplierId={s.id} dokumen={s.dokumen} />
                  </td>
                  <td>
                    {s.verified ? (
                      <div>
                        <span className="kedan-badge status-aktif inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Terverifikasi
                        </span>
                        {s.verified_at && (
                          <div className="text-[10px] text-slate-400 mt-1">
                            {formatDate(s.verified_at)}
                          </div>
                        )}
                        {s.verified_by_name && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            Oleh: {s.verified_by_name}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="kedan-badge status-nonaktif inline-flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Belum Verifikasi
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => toggleVerification(s)}
                      disabled={togglingId === s.id}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                        togglingId === s.id
                          ? "bg-slate-100 text-slate-400 cursor-wait"
                          : s.verified
                            ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                            : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                      }`}
                      data-testid={`toggle-verify-${s.id}`}
                    >
                      {togglingId === s.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : s.verified ? (
                        <><ToggleRight className="w-4 h-4" /> Cabut Verifikasi</>
                      ) : (
                        <><ToggleLeft className="w-4 h-4" /> Verifikasi</>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} totalItems={totalItems} perPage={perPage} onPageChange={setPage} onPerPageChange={changePerPage} />
        </>)}
      </div>
    </div>
  );
}

/* ========== User Table (original) ========== */
/* ========== Detail profil user (Supplier / Instansi) ========== */
function BarisInfo({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <div className="w-40 shrink-0 text-xs text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 min-w-0 break-words">{value}</div>
    </div>
  );
}

function KotakAngka({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="font-display text-lg font-bold text-slate-900">{value ?? 0}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function UserProfileModal({ detail, onClose }) {
  const { row, data, loading, error } = detail;
  const profil = data?.profil;
  const ringkasan = data?.ringkasan || {};
  const akun = data?.user || row;
  const alamatLengkap = profil
    ? [profil.alamat, profil.kelurahan, profil.kecamatan, profil.kota, profil.provinsi].filter(Boolean).join(", ")
    : "";

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="user-detail-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <div className="min-w-0">
            <h3 className="font-display font-semibold truncate">{row.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`kedan-badge ${ROLE_COLOR[row.role]}`}>{row.role}</span>
              <span className={`kedan-badge ${row.is_active ? "status-aktif" : "status-nonaktif"}`}>
                {row.is_active ? "Aktif" : "Nonaktif"}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {loading && <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8" />)}</div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}

          {!loading && !error && (
            <>
              <section>
                <div className="kedan-label text-slate-500 mb-2">Data Akun</div>
                <div className="rounded-lg border border-slate-200 px-4 py-2">
                  <BarisInfo label="Email" value={<span className="font-mono text-xs">{akun.email}</span>} />
                  <BarisInfo label="No. HP" value={akun.phone} />
                  <BarisInfo label="Organisasi" value={akun.organization} />
                  <BarisInfo label="ID SPPG" value={akun.id_sppg ? <span className="font-mono text-xs">{akun.id_sppg}</span> : null} />
                  <BarisInfo label="Bergabung" value={formatDate(akun.created_at)} />
                </div>
              </section>

              {data?.jenis === "supplier" && (
                <section>
                  <div className="kedan-label text-slate-500 mb-2 flex items-center gap-1.5">
                    <Store className="w-3.5 h-3.5" /> Profil Usaha
                  </div>
                  {!profil ? (
                    <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                      Profil usaha belum diisi oleh supplier ini.
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-slate-200 px-4 py-2">
                        <BarisInfo label="Nama Usaha" value={profil.nama_usaha} />
                        <BarisInfo
                          label="Jenis Produk"
                          value={
                            (profil.jenis_produk || []).length ? (
                              <div className="flex flex-wrap gap-1">
                                {profil.jenis_produk.map((j) => (
                                  <span key={j} className="kedan-badge bg-slate-100 text-slate-700 border-slate-200">{j}</span>
                                ))}
                              </div>
                            ) : null
                          }
                        />
                        <BarisInfo label="Alamat" value={alamatLengkap} />
                        <BarisInfo label="No. HP Usaha" value={profil.no_hp} />
                        <BarisInfo label="PIC" value={profil.nama_pic} />
                        <BarisInfo label="Koordinat" value={profil.lat != null ? <span className="font-mono text-xs">{profil.lat}, {profil.lng}</span> : null} />
                        <BarisInfo
                          label="Verifikasi"
                          value={
                            profil.verified ? (
                              <span className="inline-flex items-center gap-1.5 text-emerald-700">
                                <ShieldCheck className="w-4 h-4" /> Terverifikasi
                                {profil.verified_by_name ? ` oleh ${profil.verified_by_name}` : ""}
                                {profil.verified_at ? ` · ${formatDate(profil.verified_at)}` : ""}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-amber-700">
                                <ShieldOff className="w-4 h-4" /> Belum diverifikasi
                              </span>
                            )
                          }
                        />
                        <BarisInfo label="Didaftarkan oleh" value={profil.registered_by_name} />
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                        <KotakAngka label="Produk" value={ringkasan.jumlah_produk} />
                        <KotakAngka label="Total Stok" value={ringkasan.total_stok} />
                        <KotakAngka label="Pesanan Masuk" value={ringkasan.po_diterima} />
                        <KotakAngka label="Pesanan Selesai" value={ringkasan.po_selesai} />
                      </div>

                      {ringkasan.dokumen && (
                        <div className="mt-3">
                          <div className="kedan-label text-slate-500 mb-2 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" /> Dokumen Verifikasi
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(ringkasan.dokumen_label || {}).map(([jenis, label]) => (
                              <span
                                key={jenis}
                                className={`kedan-badge ${ringkasan.dokumen[jenis]
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-slate-100 text-slate-500 border-slate-200"}`}
                              >
                                {ringkasan.dokumen[jenis] ? "✓" : "—"} {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {data?.jenis === "instansi" && (
                <section>
                  <div className="kedan-label text-slate-500 mb-2 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> Profil Instansi
                  </div>
                  {!profil ? (
                    <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                      Profil instansi belum diisi oleh user ini.
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-slate-200 px-4 py-2">
                        <BarisInfo label="Nama Instansi" value={profil.nama_instansi} />
                        <BarisInfo label="Jenis Instansi" value={profil.jenis_instansi} />
                        <BarisInfo label="Alamat" value={alamatLengkap} />
                        <BarisInfo label="No. Telepon" value={profil.no_telp} />
                        <BarisInfo label="Email Instansi" value={profil.email_instansi} />
                        <BarisInfo label="Website" value={profil.website} />
                        <BarisInfo label="PIC" value={[profil.nama_pic, profil.jabatan_pic].filter(Boolean).join(" — ")} />
                        <BarisInfo label="Wilayah Kerja" value={profil.wilayah_kerja} />
                        <BarisInfo label="Koordinat" value={profil.lat != null ? <span className="font-mono text-xs">{profil.lat}, {profil.lng}</span> : null} />
                      </div>
                      {row.role === "SPPG" && (
                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <KotakAngka label="Pesanan Dibuat" value={ringkasan.po_dibuat} />
                          <KotakAngka label="Pesanan Selesai" value={ringkasan.po_selesai} />
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {data?.catatan && (
                <div className="text-sm text-slate-500">{data.catatan}</div>
              )}
            </>
          )}

          <div className="flex justify-end pt-1">
            <button onClick={onClose} className="kedan-btn-secondary">Tutup</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== Impor user massal dari CSV ========== */
function ImportUserModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [hasil, setHasil] = useState(null);
  const [error, setError] = useState("");

  const unduhTemplate = async () => {
    setBusy("template");
    setError("");
    try {
      const res = await api.get("/users/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "template-impor-user.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const kirim = async () => {
    if (!file) return;
    setBusy("impor");
    setError("");
    setHasil(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/import", fd);
      setHasil(data);
      if (data.ditambah) await onDone();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="import-user-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold">Impor User dari CSV</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 space-y-2">
            <div className="font-medium text-slate-700">Ketentuan berkas</div>
            <ul className="list-disc list-inside space-y-1 text-xs leading-relaxed">
              <li>Baris pertama berupa judul kolom: <span className="font-mono">email, nama, role, password, no_hp, organisasi, id_sppg, status</span>.</li>
              <li>Kolom <span className="font-mono">email</span>, <span className="font-mono">nama</span>, <span className="font-mono">role</span>, dan <span className="font-mono">password</span> wajib diisi (password minimal 8 karakter).</li>
              <li><span className="font-mono">id_sppg</span> hanya dipakai bila role-nya SPPG, dan harus unik.</li>
              <li><span className="font-mono">status</span> diisi Aktif atau Nonaktif — dikosongkan berarti Aktif.</li>
              <li>Maksimal 500 baris, ukuran berkas maksimal 2 MB.</li>
            </ul>
            <button onClick={unduhTemplate} disabled={!!busy} className="kedan-btn-secondary !py-1 !px-2 text-xs mt-1" data-testid="import-user-template">
              {busy === "template" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
              Unduh Template CSV
            </button>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Berkas CSV</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setHasil(null); setError(""); }}
              className="kedan-input !py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-xs file:text-slate-700"
              data-testid="import-user-file"
            />
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}

          {hasil && (
            <div className="space-y-3" data-testid="import-user-result">
              <div className={`rounded-md px-3 py-2 text-sm border ${
                hasil.ditambah ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"
              }`}>
                {hasil.message}
              </div>
              {hasil.gagal > 0 && (
                <div className="rounded-md border border-slate-200 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-600">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Baris yang dilewati
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {hasil.detail_gagal.map((g, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-1.5 text-slate-400 w-16">Baris {g.baris}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-600">{g.email}</td>
                          <td className="px-3 py-1.5 text-red-600">{g.alasan}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="kedan-btn-secondary">Tutup</button>
            <button
              type="button"
              onClick={kirim}
              disabled={!file || !!busy}
              className={`kedan-btn-primary ${!file || busy ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="import-user-submit"
            >
              {busy === "impor" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy === "impor" ? "Mengimpor..." : "Impor"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== Impor profil SPPG massal dari Excel ========== */
function ImportProfilSppgModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [hasil, setHasil] = useState(null);
  const [error, setError] = useState("");

  const unduhTemplate = async () => {
    setBusy("template");
    setError("");
    try {
      const res = await api.get("/agencies/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "template-impor-profil-sppg.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const kirim = async () => {
    if (!file) return;
    setBusy("impor");
    setError("");
    setHasil(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/agencies/import", fd);
      setHasil(data);
    } catch (e) {
      // Galat dari respons blob/JSON sama-sama ditampilkan sebagai teks.
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const berhasil = hasil ? hasil.dibuat + hasil.diperbarui : 0;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="import-profil-sppg-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold">Impor Profil SPPG dari Excel</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 space-y-2">
            <div className="font-medium text-slate-700">Langkah</div>
            <ol className="list-decimal list-inside space-y-1 text-xs leading-relaxed">
              <li>Unduh template — sudah berisi semua akun SPPG beserta profil yang sudah ada.</li>
              <li>Lengkapi kolom bertanda <span className="font-mono">*</span>: Jenis SPPG, No. Telepon, Kabupaten/Kota, Kecamatan, Kelurahan/Desa, Alamat Lengkap, Latitude, Longitude.</li>
              <li>Unggah kembali berkasnya. Setiap baris dicocokkan ke akun lewat <b>ID SPPG</b> (atau <b>Email Akun</b> bila ID kosong).</li>
            </ol>
            <ul className="list-disc list-inside space-y-1 text-xs leading-relaxed">
              <li>Nama &amp; email profil selalu mengikuti akun di Manajemen User.</li>
              <li>Nama wilayah harus sesuai lembar <i>Referensi</i> di template. Koordinat memakai titik desimal, mis. 3.5952 dan 98.6722.</li>
              <li>Profil yang sudah ada akan diperbarui; kolom tidak wajib yang dikosongkan tidak menghapus isian lama. Baris tanpa isian dilewati.</li>
              <li>Format <span className="font-mono">.xlsx</span> atau <span className="font-mono">.csv</span>, maksimal 1.000 baris / 5 MB.</li>
            </ul>
            <button onClick={unduhTemplate} disabled={!!busy} className="kedan-btn-secondary !py-1 !px-2 text-xs mt-1" data-testid="import-profil-template">
              {busy === "template" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
              Unduh Template Excel
            </button>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Berkas Excel / CSV</label>
            <input
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setHasil(null); setError(""); }}
              className="kedan-input !py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-xs file:text-slate-700"
              data-testid="import-profil-file"
            />
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}

          {hasil && (
            <div className="space-y-3" data-testid="import-profil-result">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ["Dibuat", hasil.dibuat, "text-emerald-700"],
                  ["Diperbarui", hasil.diperbarui, "text-[#045498]"],
                  ["Gagal", hasil.gagal, hasil.gagal ? "text-red-600" : "text-slate-700"],
                  ["Baris kosong", hasil.dilewati, "text-slate-500"],
                ].map(([label, n, warna]) => (
                  <div key={label} className="rounded-md border border-slate-200 px-3 py-2">
                    <div className="text-[11px] text-slate-500">{label}</div>
                    <div className={`font-display text-xl font-bold ${warna}`}>{n}</div>
                  </div>
                ))}
              </div>
              <div className={`rounded-md px-3 py-2 text-sm border ${
                berhasil ? "!bg-emerald-50 !border-emerald-200 text-emerald-800" : "!bg-amber-50 !border-amber-200 text-amber-800"
              }`}>
                {hasil.message}
              </div>
              {hasil.gagal > 0 && (
                <div className="rounded-md border border-slate-200 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-600">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Baris yang gagal — perbaiki di berkas lalu unggah ulang
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {hasil.detail_gagal.map((g, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0 align-top">
                            <td className="px-3 py-1.5 text-slate-400 w-16 whitespace-nowrap">Baris {g.baris}</td>
                            <td className="px-3 py-1.5 font-mono text-slate-600">{g.email}</td>
                            <td className="px-3 py-1.5 text-red-600">{g.alasan}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {hasil.gagal > hasil.detail_gagal.length && (
                    <div className="px-3 py-1.5 text-[11px] text-slate-500 border-t border-slate-100">
                      Menampilkan {hasil.detail_gagal.length} dari {hasil.gagal} baris gagal.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="kedan-btn-secondary">Tutup</button>
            <button
              type="button"
              onClick={kirim}
              disabled={!file || !!busy}
              className={`kedan-btn-primary ${!file || busy ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="import-profil-submit"
            >
              {busy === "impor" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy === "impor" ? "Mengimpor..." : "Impor"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserTable({ rows, loading, search, setSearch, roleFilter, setRoleFilter, openEdit, openDetail, remove, currentUserId }) {
  const filtered = rows.filter((r) => {
    if (roleFilter && r.role !== roleFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !r.name.toLowerCase().includes(q) &&
        !r.email.toLowerCase().includes(q) &&
        !(r.organization || "").toLowerCase().includes(q) &&
        !(r.id_sppg || "").toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });
  const { page, setPage, perPage, changePerPage, totalPages, paged, totalItems } = usePagination(filtered, 10);

  return (
    <div className="kedan-card overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        <div className="flex gap-1.5">
          {["", ...ROLES].map((r) => (
            <button key={r} onClick={() => { setRoleFilter(r); setPage(1); }}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                roleFilter === r ? "bg-[#045498] text-white border-[#045498]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`filter-role-${r || "all"}`}>
              {r || "Semua"}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            className="kedan-input !w-56 !py-1.5 !pl-8 text-xs"
            placeholder="Cari nama, email, organisasi..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            data-testid="search-user"
          />
        </div>
        <div className="text-xs text-slate-500">{filtered.length} user</div>
      </div>

      {loading ? (
        <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="Tidak ada user ditemukan" icon={UserCog} />
      ) : (<>
        <table className="kedan-table">
          <thead><tr>
            <th>Nama</th><th>Email</th><th>Role</th><th>Organisasi</th>
            <th>Status</th><th>Bergabung</th><th className="text-right">Aksi</th>
          </tr></thead>
          <tbody data-testid="user-table-body">
            {paged.map((r) => (
              <tr key={r.id}>
                <td className="font-medium">
                  {r.name}
                  {r.id_sppg && (
                    <div className="text-[11px] font-mono font-normal text-slate-400 mt-0.5" title="ID SPPG">
                      {r.id_sppg}
                    </div>
                  )}
                </td>
                <td className="font-mono text-xs">{r.email}</td>
                <td><span className={`kedan-badge ${ROLE_COLOR[r.role]}`}>{r.role}</span></td>
                <td className="text-xs text-slate-600">{r.organization || "-"}</td>
                <td>
                  <span className={`kedan-badge ${r.is_active ? "status-aktif" : "status-nonaktif"}`}>
                    {r.is_active ? "Aktif" : "Nonaktif"}
                  </span>
                </td>
                <td className="text-xs">{formatDate(r.created_at)}</td>
                <td className="text-right">
                  <div className="inline-flex gap-2">
                    {ROLE_BERPROFIL.includes(r.role) && (
                      <button
                        onClick={() => openDetail(r)}
                        className="p-1.5 hover:bg-blue-50 rounded text-[#045498]"
                        title={`Lihat profil ${r.role}`}
                        data-testid={`detail-user-${r.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded text-slate-600" data-testid={`edit-user-${r.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    {r.id !== currentUserId && (
                      <button onClick={() => remove(r)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`delete-user-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} totalPages={totalPages} totalItems={totalItems} perPage={perPage} onPageChange={setPage} onPerPageChange={changePerPage} />
      </>)}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, disabled, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 mb-1 block">{label}</label>
      <input type={type} value={value} disabled={disabled} required={required}
        onChange={(e) => onChange(e.target.value)} className="kedan-input"
        data-testid={testid} />
    </div>
  );
}
